import { Sparkles, Wand2, Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react'
import { useCoverAiGeneration, AI_MODELS } from '../hooks/useCoverAiGeneration'
import type { Asset } from '../services/api'

interface AiGenerationPanelProps {
  coverId: string
  coverWidth: number
  coverHeight: number
  paletteAssets: Asset[]
  userPoints: number
  onGenerationComplete: (assetId: string) => void
}

export function AiGenerationPanel({
  coverId,
  coverWidth,
  coverHeight,
  paletteAssets,
  userPoints,
  onGenerationComplete,
}: AiGenerationPanelProps) {
  const {
    selectedModel,
    setSelectedModel,
    selectedModelInfo,
    selectedAssetIds,
    toggleAssetSelection,
    clearAssetSelection,
    generatedPrompt,
    setGeneratedPrompt,
    customInstructions,
    setCustomInstructions,
    generatePrompt,
    generateCover,
    reset,
    isGeneratingPrompt,
    isGeneratingCover,
    generatedAssetId,
    generationCost,
    pointsRemaining,
    error,
    setError,
  } = useCoverAiGeneration({
    coverId,
    coverWidth,
    coverHeight,
    onGenerationComplete,
  })

  const hasInsufficientPoints = userPoints < selectedModelInfo.cost

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-neutral-800">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-accent-primary" />
          <div>
            <h2 className="text-xl font-bold text-neutral-100">AI 표지 생성</h2>
            <p className="text-sm text-neutral-400">에셋을 조합하여 AI로 표지를 생성합니다</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-neutral-400">보유 포인트</p>
          <p className="text-lg font-bold text-accent-primary">{userPoints.toLocaleString()}P</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Model Selection */}
        <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-medium text-neutral-200">AI 모델 선택</label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={isGeneratingPrompt || isGeneratingCover}
            className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50"
          >
            {AI_MODELS.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name} - {model.cost}P ({model.description})
              </option>
            ))}
          </select>
          {hasInsufficientPoints && (
            <div className="flex items-center gap-2 text-accent-error text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>포인트가 부족합니다. (필요: {selectedModelInfo.cost}P)</span>
            </div>
          )}
        </div>

        {/* Asset Selection */}
        <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-neutral-200">
              참조 에셋 선택 ({selectedAssetIds.length}개 선택됨)
            </label>
            {selectedAssetIds.length > 0 && (
              <button
                onClick={clearAssetSelection}
                className="text-xs text-neutral-400 hover:text-neutral-200"
              >
                선택 해제
              </button>
            )}
          </div>
          <p className="text-xs text-neutral-400">
            표지에 포함할 에셋을 선택하세요 (최대 10개)
          </p>

          {paletteAssets.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              팔레트에 에셋이 없습니다. 먼저 에셋을 추가해주세요.
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 max-h-64 overflow-y-auto">
              {paletteAssets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => toggleAssetSelection(asset.id)}
                  disabled={
                    selectedAssetIds.length >= 10 && !selectedAssetIds.includes(asset.id)
                  }
                  className={`
                    relative aspect-square rounded-lg overflow-hidden border-2 transition-all
                    ${
                      selectedAssetIds.includes(asset.id)
                        ? 'border-accent-primary ring-2 ring-accent-primary/50'
                        : 'border-neutral-700 hover:border-neutral-600'
                    }
                    disabled:opacity-40 disabled:cursor-not-allowed
                  `}
                >
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedAssetIds.includes(asset.id) && (
                    <div className="absolute inset-0 bg-accent-primary/20 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-accent-primary" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5">
                    <p className="text-xs text-white truncate">{asset.name}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Custom Instructions */}
        <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-medium text-neutral-200">추가 지시사항 (선택)</label>
          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="예: 어두운 분위기, 미니멀한 스타일 등..."
            disabled={isGeneratingPrompt || isGeneratingCover}
            rows={2}
            className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 resize-none"
          />
          <button
            onClick={generatePrompt}
            disabled={selectedAssetIds.length === 0 || isGeneratingPrompt || isGeneratingCover}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-accent-secondary text-neutral-900 rounded-md hover:bg-accent-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGeneratingPrompt ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>프롬프트 생성 중...</span>
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                <span>에셋으로 프롬프트 생성</span>
              </>
            )}
          </button>
        </div>

        {/* Generated Prompt */}
        <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-medium text-neutral-200">생성 프롬프트</label>
          <textarea
            value={generatedPrompt}
            onChange={(e) => setGeneratedPrompt(e.target.value)}
            placeholder="프롬프트를 직접 입력하거나 위에서 에셋을 선택한 후 '프롬프트 생성' 버튼을 클릭하세요."
            disabled={isGeneratingPrompt || isGeneratingCover}
            rows={6}
            className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 resize-none"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-accent-error/10 border border-accent-error rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-accent-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-accent-error">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-accent-error hover:text-accent-error/80"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success Message */}
        {generatedAssetId && (
          <div className="bg-accent-success/10 border border-accent-success rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent-success flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-accent-success font-medium">표지 생성 완료!</p>
                <p className="text-xs text-neutral-400 mt-1">
                  {generationCost.toFixed(3)}$ 사용 · 남은 포인트: {pointsRemaining.toLocaleString()}P
                </p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-neutral-700">
              <img
                src={`/api/v1/assets/${generatedAssetId}/image`}
                alt="Generated cover"
                className="w-full"
              />
            </div>
            <button
              onClick={reset}
              className="w-full px-4 py-2 bg-neutral-800 text-neutral-200 rounded-md hover:bg-neutral-700 transition-colors"
            >
              새로 생성하기
            </button>
          </div>
        )}
      </div>

      {/* Footer - Generate Button */}
      {!generatedAssetId && (
        <div className="border-t border-neutral-800 p-6">
          <button
            onClick={generateCover}
            disabled={
              !generatedPrompt.trim() ||
              isGeneratingPrompt ||
              isGeneratingCover ||
              hasInsufficientPoints
            }
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-accent-primary text-white rounded-md hover:bg-accent-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGeneratingCover ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>표지 생성 중... (최대 60초 소요)</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>표지 생성 ({selectedModelInfo.cost}P 사용)</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
